// src/views/ConfiguradorPreciosView.js
// ───────────────────────────────────────────────────────────────────────────
// 🛠️ CONFIGURADOR DE PRECIOS (Fase 2 — UI). Edita EN VIVO la verdad de precio del motor.
//
// REGLAS INVIOLABLES:
//   • LEE por el path de lectura existente: getPricingConfig (callable read-only; pricingConfig
//     es server-only por rules), `materials` (getDocs) e `itc_events` (query, lectura permitida).
//   • ESCRIBE ÚNICAMENTE por las 2 CF de Fase 1 (updatePricingConfig / updateMaterialStockPrices).
//     NUNCA setDoc/updateDoc/addDoc desde el cliente. La validación REAL es la del servidor; la
//     del cliente es solo UX (feedback inmediato).
//   • No duplica la verdad de precio (edita las fuentes que el motor ya lee). El preview SPP usa
//     el espejo de computeSppMsf (src/lib/sppPreview.js), pineado al servidor por parity test.
// ───────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebase-config.js';
import { computeSppMsf } from '../lib/sppPreview.js';

// ── helpers de edición inmutable por path ─────────────────────────────────────
const clone = (o) => (o == null ? o : JSON.parse(JSON.stringify(o)));
const getIn = (obj, path) => path.reduce((o, k) => (o == null ? undefined : o[k]), obj);
function setIn(obj, path, value) {
  if (!path.length) return value;
  const [h, ...rest] = path;
  const base = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  base[h] = setIn(obj ? obj[h] : undefined, rest, value);
  return base;
}
// Lista de paths con string vacío (campos numéricos sin completar) — validación UX previa al save.
function collectEmpties(obj, base = []) {
  let out = [];
  if (Array.isArray(obj)) obj.forEach((v, i) => { out = out.concat(collectEmpties(v, [...base, i])); });
  else if (obj && typeof obj === 'object') Object.keys(obj).forEach((k) => { out = out.concat(collectEmpties(obj[k], [...base, k])); });
  else if (obj === '') out.push(base.join('.'));
  return out;
}

// ── input numérico (centrado, per memoria de columnas numéricas) ──────────────
const Num = ({ value, onChange, step = 'any', w = 'w-24' }) => (
  <input
    type="number" step={step} value={value ?? ''}
    onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
    className={`${w} p-1.5 border border-gray-300 rounded text-center text-sm tabular-nums`}
  />
);
const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-xs font-medium text-gray-600">{label}{hint && <span className="text-gray-400 font-normal"> · {hint}</span>}</span>
    {children}
  </label>
);
const Panel = ({ title, children, right }) => (
  <div className="border border-gray-200 rounded-lg p-4 bg-white">
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
      {right}
    </div>
    {children}
  </div>
);

const FLUTES = ['B', 'C', 'E', 'BC', 'EB', 'BE', 'N'];
const LINERS = ['KRAFT', 'WHITE', 'KEMI'];

export default function ConfiguradorPreciosView({ db, user }) {
  const [tab, setTab] = useState('costos');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cfg, setCfg] = useState(null);            // efectivo del servidor (SEED ⊕ doc)
  const [draft, setDraft] = useState(null);        // copia editable { costModel, material }
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);    // { type:'ok'|'error', text }

  const [materials, setMaterials] = useState([]);
  const [events, setEvents] = useState([]);

  const call = useCallback((name, data) => httpsCallable(functions, name)(data).then((r) => r.data), []);

  const loadConfig = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await call('getPricingConfig', {});
      setCfg(data);
      setDraft({ costModel: clone(data.costModel), material: clone(data.material),
        estimateCounter: clone(data.estimateCounter || { seedYear: new Date().getFullYear(), seedNumber: 1, defaultStart: 1 }) });
    } catch (e) { setError(e.message || 'No se pudo cargar la configuración de precio.'); }
    finally { setLoading(false); }
  }, [call]);

  const loadMaterials = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'materials'));
      setMaterials(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.displayName || a.name || a.code || '').localeCompare(b.displayName || b.name || b.code || '')));
    } catch (_) { /* materiales opcionales para esta vista */ }
  }, [db]);

  const loadBitacora = useCallback(async () => {
    try {
      const q = query(collection(db, 'itc_events'), orderBy('timestamp', 'desc'), limit(100));
      const snap = await getDocs(q);
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((e) => e.event === 'pricing.updated'));
    } catch (_) { /* bitácora best-effort */ }
  }, [db]);

  useEffect(() => { loadConfig(); loadMaterials(); loadBitacora(); }, [loadConfig, loadMaterials, loadBitacora]);

  const setPath = (path, value) => setDraft((d) => setIn(d, path, value));

  // ── guardar una sección de pricingConfig (costModel | material) por la CF ──────
  const saveConfig = async (section) => {
    const empties = collectEmpties(draft[section], [section]);
    if (empties.length) { setSaveMsg({ type: 'error', text: `Completá los campos vacíos: ${empties.slice(0, 4).join(', ')}${empties.length > 4 ? '…' : ''}` }); return; }
    setSaving(true); setSaveMsg(null);
    try {
      const res = await call('updatePricingConfig', { patch: { [section]: draft[section] } });
      setSaveMsg({ type: 'ok', text: res.changed ? `Guardado — ${res.changed} cambio(s) en ${res.sections?.join(', ')}.` : 'Sin cambios para guardar.' });
      await loadConfig(); await loadBitacora();
    } catch (e) { setSaveMsg({ type: 'error', text: e.message || 'No se pudo guardar (rechazado por el servidor).' }); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-gray-500">Cargando configuración de precio…</div>;
  if (error) return (
    <div className="p-8">
      <p className="text-red-600 font-medium">{error}</p>
      <button onClick={loadConfig} className="mt-3 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700">Reintentar</button>
    </div>
  );

  const tabs = [
    ['costos', 'Costos del motor'],
    ['spp', 'Matriz SPP'],
    ['stock', 'Precios de stock'],
    ['contador', 'Contador de estimados'],
    ['bitacora', 'Bitácora'],
  ];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <header className="mb-4">
        <h1 className="text-3xl font-bold text-gray-800">Configurador de precios</h1>
        <p className="text-sm text-gray-500 mt-1">
          Edita la verdad de precio del motor en vivo. Escribe solo por Cloud Function (con bitácora).
          {cfg && <> · rev <strong>{cfg.rev}</strong>{cfg.updatedBy ? <> · últ. {cfg.updatedBy}</> : null}
            {cfg.updatedAt ? <> · {new Date(cfg.updatedAt).toLocaleString()}</> : null}</>}
        </p>
      </header>

      <div className="flex gap-1 border-b border-gray-200 mb-4">
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => { setTab(id); setSaveMsg(null); }}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg ${tab === id ? 'bg-white border border-b-white border-gray-200 text-blue-700 -mb-px' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {saveMsg && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${saveMsg.type === 'ok' ? 'bg-green-50 border border-green-300 text-green-800' : 'bg-red-50 border border-red-300 text-red-800'}`}>
          {saveMsg.text}
        </div>
      )}

      {tab === 'costos' && <CostosTab draft={draft} setPath={setPath} saving={saving} onSave={() => saveConfig('costModel')} />}
      {tab === 'spp' && <SppTab draft={draft} setPath={setPath} saving={saving} onSave={() => saveConfig('material')} />}
      {tab === 'stock' && <StockTab db={db} materials={materials} call={call} onSaved={() => { loadMaterials(); loadBitacora(); }} setSaveMsg={setSaveMsg} saving={saving} setSaving={setSaving} />}
      {tab === 'contador' && <ContadorTab draft={draft} setPath={setPath} saving={saving} onSave={() => saveConfig('estimateCounter')}
        call={call} setSaveMsg={setSaveMsg} onAfterReset={loadBitacora} canManage={!!user?.permissions?.canManagePricing} />}
      {tab === 'bitacora' && <BitacoraTab events={events} onReload={loadBitacora} />}
    </div>
  );
}

// ════════════════════════ TAB: Costos del motor (costModel) ════════════════════
function CostosTab({ draft, setPath, saving, onSave }) {
  const cm = draft.costModel || {};
  const p = (sub) => ['costModel', ...sub];
  const derivedHour = (Number(cm.overheadMensual) || 0) / (Number(cm.horasMaquinaMes) || 1);
  const modos = (cm.impresion && cm.impresion.modos) || {};

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <Panel title="Máquina (overhead)">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Overhead mensual" hint="$/mes"><Num value={cm.overheadMensual} onChange={(v) => setPath(p(['overheadMensual']), v)} /></Field>
            <Field label="Horas-máquina/mes"><Num value={cm.horasMaquinaMes} onChange={(v) => setPath(p(['horasMaquinaMes']), v)} /></Field>
            <Field label="Costo $/hora" hint={`derivado ${derivedHour.toFixed(2)}`}><Num value={cm.costHourRate} onChange={(v) => setPath(p(['costHourRate']), v)} /></Field>
            <div className="flex items-end"><button type="button" onClick={() => setPath(p(['costHourRate']), Math.round(derivedHour * 100) / 100)} className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">Usar derivado</button></div>
          </div>
        </Panel>

        <Panel title="Corte (rule-based)">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Vel. corte" hint="in/min"><Num value={getIn(cm, ['corte', 'cutSpeedInPorMin'])} onChange={(v) => setPath(p(['corte', 'cutSpeedInPorMin']), v)} /></Field>
            <Field label="Vel. signado" hint="in/min"><Num value={getIn(cm, ['corte', 'creaseSpeedInPorMin'])} onChange={(v) => setPath(p(['corte', 'creaseSpeedInPorMin']), v)} /></Field>
            <Field label="Cambio hoja" hint="min/plancha"><Num value={getIn(cm, ['corte', 'cambioMinPorPlancha'])} onChange={(v) => setPath(p(['corte', 'cambioMinPorPlancha']), v)} /></Field>
            <Field label="Setup" hint="min"><Num value={getIn(cm, ['corte', 'setupMin'])} onChange={(v) => setPath(p(['corte', 'setupMin']), v)} /></Field>
          </div>
        </Panel>
      </div>

      <Panel title="Impresión + modos">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
          <Field label="Throughput base" hint="planchas/6h"><Num value={getIn(cm, ['impresion', 'throughputBasePlanchasPor6h'])} onChange={(v) => setPath(p(['impresion', 'throughputBasePlanchasPor6h']), v)} /></Field>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 uppercase"><tr><th className="text-left py-1">Modo</th><th className="py-1">velX</th><th className="py-1">tintaX</th></tr></thead>
          <tbody>
            {['draft', 'produccion', 'quality'].map((m) => (
              <tr key={m} className="border-t border-gray-100">
                <td className="py-1.5 font-medium text-gray-700 capitalize">{m}</td>
                <td className="py-1.5 text-center"><Num w="w-20" value={getIn(modos, [m, 'velX'])} onChange={(v) => setPath(p(['impresion', 'modos', m, 'velX']), v)} /></td>
                <td className="py-1.5 text-center"><Num w="w-20" value={getIn(modos, [m, 'tintaX'])} onChange={(v) => setPath(p(['impresion', 'modos', m, 'tintaX']), v)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-gray-100">
          <Field label="Blanco velX"><Num value={getIn(cm, ['impresion', 'blanco', 'velX'])} onChange={(v) => setPath(p(['impresion', 'blanco', 'velX']), v)} /></Field>
          <Field label="Blanco tinta+" hint="ml/ft²"><Num value={getIn(cm, ['impresion', 'blanco', 'tintaExtraMlFt2'])} onChange={(v) => setPath(p(['impresion', 'blanco', 'tintaExtraMlFt2']), v)} /></Field>
          <Field label="2 caras tiempoX"><Num value={getIn(cm, ['impresion', 'dosCaras', 'tiempoX'])} onChange={(v) => setPath(p(['impresion', 'dosCaras', 'tiempoX']), v)} /></Field>
          <Field label="2 caras tintaX"><Num value={getIn(cm, ['impresion', 'dosCaras', 'tintaX'])} onChange={(v) => setPath(p(['impresion', 'dosCaras', 'tintaX']), v)} /></Field>
        </div>
      </Panel>

      <div className="grid md:grid-cols-3 gap-4">
        <Panel title="Tinta">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Base" hint="ml/ft²"><Num value={getIn(cm, ['tinta', 'baseMlFt2'])} onChange={(v) => setPath(p(['tinta', 'baseMlFt2']), v)} /></Field>
            <Field label="Costo" hint="$/ml"><Num value={getIn(cm, ['tinta', 'costoPorMl'])} onChange={(v) => setPath(p(['tinta', 'costoPorMl']), v)} /></Field>
          </div>
        </Panel>
        <Panel title="Material / Cuchilla / Armado">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Merma" hint="0–1"><Num value={getIn(cm, ['material', 'mermaPct'])} onChange={(v) => setPath(p(['material', 'mermaPct']), v)} /></Field>
            <Field label="Cuchilla $"><Num value={getIn(cm, ['cuchilla', 'costo'])} onChange={(v) => setPath(p(['cuchilla', 'costo']), v)} /></Field>
            <Field label="Cuchilla rinde" hint="hojas"><Num value={getIn(cm, ['cuchilla', 'rindeHojas'])} onChange={(v) => setPath(p(['cuchilla', 'rindeHojas']), v)} /></Field>
            <Field label="Armado" hint="$/h"><Num value={cm.tarifaArmadoPorHora} onChange={(v) => setPath(p(['tarifaArmadoPorHora']), v)} /></Field>
          </div>
        </Panel>
        <Panel title="Markup escala" right={<button type="button" onClick={() => setPath(p(['markup', 'escala']), [...(getIn(cm, ['markup', 'escala']) || []), 0])} className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">+ tramo</button>}>
          <div className="flex flex-wrap gap-2">
            {(getIn(cm, ['markup', 'escala']) || []).map((val, i) => (
              <Num key={i} w="w-20" value={val} onChange={(v) => setPath(p(['markup', 'escala', i]), v)} />
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">No decreciente. Piso = costo.</p>
        </Panel>
      </div>

      <div className="flex justify-end">
        <button onClick={onSave} disabled={saving} className="px-5 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Guardando…' : 'Guardar costos del motor'}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════ TAB: Matriz SPP (material) ═══════════════════════════
function SppTab({ draft, setPath, saving, onSave }) {
  const mat = draft.material || {};
  const sm = mat.sppMatrix || {};
  const pm = (sub) => ['material', ...sub];

  // Preview en vivo: corre el ESPEJO de computeSppMsf contra la matriz EDITADA (sin guardar).
  const [pv, setPv] = useState({ test: '200#', flute: 'E', outerLiner: 'WHITE', innerLiner: 'WHITE', sheetL: 48, sheetW: 96, sheets: 1 });
  const preview = useMemo(() => {
    try { return computeSppMsf(pv, sm); } catch (_) { return null; }
  }, [pv, sm]);

  return (
    <div className="space-y-4">
      <Panel title="Preview en vivo del combo (usa computeSppMsf real, sobre la matriz editada)">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Test"><input value={pv.test} onChange={(e) => setPv({ ...pv, test: e.target.value })} className="w-24 p-1.5 border border-gray-300 rounded text-center text-sm" /></Field>
          <Field label="Flute">
            <select value={pv.flute} onChange={(e) => setPv({ ...pv, flute: e.target.value })} className="w-24 p-1.5 border border-gray-300 rounded text-sm">
              {FLUTES.map((f) => <option key={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Liner ext.">
            <select value={pv.outerLiner} onChange={(e) => setPv({ ...pv, outerLiner: e.target.value })} className="w-28 p-1.5 border border-gray-300 rounded text-sm">
              {LINERS.map((l) => <option key={l}>{l}</option>)}
            </select>
          </Field>
          <Field label="Liner int.">
            <select value={pv.innerLiner} onChange={(e) => setPv({ ...pv, innerLiner: e.target.value })} className="w-28 p-1.5 border border-gray-300 rounded text-sm">
              {LINERS.map((l) => <option key={l}>{l}</option>)}
            </select>
          </Field>
          <Field label="Hoja L×W"><div className="flex gap-1"><Num w="w-16" value={pv.sheetL} onChange={(v) => setPv({ ...pv, sheetL: v })} /><Num w="w-16" value={pv.sheetW} onChange={(v) => setPv({ ...pv, sheetW: v })} /></div></Field>
          <Field label="Hojas"><Num w="w-16" value={pv.sheets} onChange={(v) => setPv({ ...pv, sheets: v })} /></Field>
          <div className="ml-auto text-right">
            <div className="text-xs text-gray-500">$/MSF resuelto (pre-waste)</div>
            <div className={`text-2xl font-extrabold tabular-nums ${preview == null ? 'text-gray-400' : 'text-green-700'}`}>
              {preview == null ? 'no compone' : `$${preview.toFixed(2)}`}
            </div>
          </div>
        </div>
      </Panel>

      <Panel title="Bases (test|pared × tramos de volumen)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase"><tr><th className="text-left py-1">Base</th><th>t1</th><th>t2</th><th>t3</th><th>t4</th><th>t5</th><th className="text-left pl-3">Flutes</th></tr></thead>
            <tbody>
              {Object.keys(sm.bases || {}).map((bk) => (
                <tr key={bk} className="border-t border-gray-100">
                  <td className="py-1.5 font-mono text-xs text-gray-700 whitespace-nowrap">{bk}</td>
                  {(sm.bases[bk].msf || []).map((val, i) => (
                    <td key={i} className="py-1.5 text-center"><Num w="w-20" value={val} onChange={(v) => setPath(pm(['sppMatrix', 'bases', bk, 'msf', i]), v)} /></td>
                  ))}
                  <td className="py-1.5 pl-3">
                    <input value={(sm.bases[bk].flutes || []).join(',')}
                      onChange={(e) => setPath(pm(['sppMatrix', 'bases', bk, 'flutes']), e.target.value.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean))}
                      className="w-28 p-1 border border-gray-200 rounded text-xs" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="grid md:grid-cols-2 gap-4">
        <Panel title="Upcharges — flute">
          <div className="grid grid-cols-3 gap-2">
            {Object.keys((sm.upcharges && sm.upcharges.flute) || {}).map((f) => (
              <Field key={f} label={f}><Num w="w-20" value={sm.upcharges.flute[f]} onChange={(v) => setPath(pm(['sppMatrix', 'upcharges', 'flute', f]), v)} /></Field>
            ))}
          </div>
        </Panel>
        <Panel title="Upcharges — white top / kemi">
          <div className="grid grid-cols-2 gap-2">
            {Object.keys((sm.upcharges && sm.upcharges.whiteTop) || {}).map((w) => (
              <Field key={w} label={`White ${w}`} hint="$/MSF"><Num w="w-20" value={sm.upcharges.whiteTop[w].perMsf} onChange={(v) => setPath(pm(['sppMatrix', 'upcharges', 'whiteTop', w, 'perMsf']), v)} /></Field>
            ))}
            <Field label="Kemi" hint="$/MSF"><Num w="w-20" value={getIn(sm, ['upcharges', 'kemi', 'perMsf'])} onChange={(v) => setPath(pm(['sppMatrix', 'upcharges', 'kemi', 'perMsf']), v)} /></Field>
          </div>
        </Panel>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Panel title="Tramos / merma / fallback">
          <div className="grid grid-cols-2 gap-3">
            <Field label="msfBrackets (material)" hint="creciente">
              <div className="flex gap-1 flex-wrap">{(mat.msfBrackets || []).map((b, i) => <Num key={i} w="w-16" value={b} onChange={(v) => setPath(pm(['msfBrackets', i]), v)} />)}</div>
            </Field>
            <Field label="wasteFactor" hint="≥ 1"><Num value={mat.wasteFactor} onChange={(v) => setPath(pm(['wasteFactor']), v)} /></Field>
            <Field label="defaultMsf" hint="> 0"><Num value={mat.defaultMsf} onChange={(v) => setPath(pm(['defaultMsf']), v)} /></Field>
            <Field label="sppMatrix.wasteFactor"><Num value={sm.wasteFactor} onChange={(v) => setPath(pm(['sppMatrix', 'wasteFactor']), v)} /></Field>
          </div>
        </Panel>
        <Panel title="Overrides por-SKU (msfByMaterial)">
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase sticky top-0 bg-white"><tr><th className="text-left py-1">Clave</th><th>t1</th><th>t2</th><th>t3</th><th>t4</th><th>t5</th></tr></thead>
              <tbody>
                {Object.keys(mat.msfByMaterial || {}).map((k) => (
                  <tr key={k} className="border-t border-gray-100">
                    <td className="py-1 font-mono text-xs text-gray-700 whitespace-nowrap pr-2">{k}</td>
                    {(mat.msfByMaterial[k] || []).map((val, i) => (
                      <td key={i} className="py-1 text-center"><Num w="w-16" value={val} onChange={(v) => setPath(pm(['msfByMaterial', k, i]), v)} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <div className="flex justify-end">
        <button onClick={onSave} disabled={saving} className="px-5 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Guardando…' : 'Guardar matriz SPP + material'}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════ TAB: Precios de stock por material ════════════════════
function StockTab({ materials, call, onSaved, setSaveMsg, saving, setSaving }) {
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState(null);     // material seleccionado
  const [rows, setRows] = useState([]);     // [{ key, value }]

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return materials.slice(0, 60);
    return materials.filter((m) => `${m.displayName || ''} ${m.name || ''} ${m.code || ''} ${m.category || ''} ${m.supplier || ''}`.toLowerCase().includes(s)).slice(0, 60);
  }, [materials, search]);

  const selectMaterial = (m) => {
    setSel(m);
    const sp = m.stockPrices || {};
    setRows(Object.keys(sp).map((k) => ({ key: k, value: sp[k] })));
    setSaveMsg(null);
  };
  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    if (!sel) return;
    const sp = {};
    for (const r of rows) { const k = (r.key || '').trim(); if (k) sp[k] = r.value === '' ? '' : Number(r.value); }
    if (!Object.keys(sp).length) { setSaveMsg({ type: 'error', text: 'Agregá al menos una medida de hoja.' }); return; }
    const bad = Object.entries(sp).filter(([k, v]) => !/^\d+(?:\.\d+)?x\d+(?:\.\d+)?$/.test(k) || !(Number(v) > 0));
    if (bad.length) { setSaveMsg({ type: 'error', text: `Revisá clave/precio: ${bad.map(([k]) => k).slice(0, 3).join(', ')} (formato "WxH", precio > 0).` }); return; }
    setSaving(true); setSaveMsg(null);
    try {
      const res = await call('updateMaterialStockPrices', { materialId: sel.id, stockPrices: sp, mode: 'replace' });
      setSaveMsg({ type: 'ok', text: res.changed ? `Stock guardado — ${res.changed} cambio(s) en ${sel.displayName || sel.code || sel.id}.` : 'Sin cambios.' });
      onSaved();
    } catch (e) { setSaveMsg({ type: 'error', text: e.message || 'No se pudo guardar el stock (rechazado por el servidor).' }); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-4">
      <Panel title={`Materiales (${materials.length})`}>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar material / proveedor…" className="w-full p-2 border border-gray-300 rounded-md text-sm mb-2" />
        <div className="max-h-[28rem] overflow-y-auto divide-y divide-gray-100">
          {filtered.map((m) => (
            <button key={m.id} onClick={() => selectMaterial(m)} className={`w-full text-left px-2 py-1.5 text-sm rounded ${sel && sel.id === m.id ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50'}`}>
              <div className="font-medium truncate">{m.displayName || m.name || m.code || m.id}</div>
              <div className="text-xs text-gray-400">{m.supplier || m.category || ''}{m.stockPrices ? ` · ${Object.keys(m.stockPrices).length} medida(s)` : ' · sin precio'}</div>
            </button>
          ))}
          {!filtered.length && <p className="text-xs text-gray-400 p-2">Sin resultados.</p>}
        </div>
      </Panel>

      <Panel title={sel ? `Precios de stock — ${sel.displayName || sel.code || sel.id}` : 'Elegí un material'}
        right={sel && <button type="button" onClick={() => setRows((r) => [...r, { key: '', value: '' }])} className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">+ medida</button>}>
        {!sel && <p className="text-sm text-gray-400">Seleccioná un material de la lista para editar su $/hoja por medida.</p>}
        {sel && (
          <>
            <p className="text-xs text-gray-400 mb-2">
              {sel.dimensions ? `Nominal ${sel.dimensions.width}×${sel.dimensions.height} ${sel.dimensions.unit || 'in'} · ` : ''}
              Acepta múltiples medidas. Clave "WxH" (p.ej. 48x96), precio $/hoja &gt; 0.
            </p>
            <table className="w-full text-sm mb-3">
              <thead className="text-xs text-gray-500 uppercase"><tr><th className="text-left py-1">Medida (WxH)</th><th className="text-left py-1">$/hoja</th><th></th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="py-1.5"><input value={r.key} onChange={(e) => setRow(i, { key: e.target.value })} placeholder="48x96" className="w-28 p-1.5 border border-gray-300 rounded text-center text-sm" /></td>
                    <td className="py-1.5"><Num value={r.value} onChange={(v) => setRow(i, { value: v })} /></td>
                    <td className="py-1.5"><button type="button" onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))} className="text-xs text-red-500 hover:underline">quitar</button></td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={3} className="py-2 text-xs text-gray-400">Sin medidas. Usá “+ medida”.</td></tr>}
              </tbody>
            </table>
            <div className="flex justify-end">
              <button onClick={save} disabled={saving} className="px-5 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Guardando…' : 'Guardar precios de stock'}
              </button>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}

// ════════════════════════ TAB: Contador de estimados (estimateCounter) ════════════
function ContadorTab({ draft, setPath, saving, onSave, call, setSaveMsg, onAfterReset, canManage }) {
  const ec = draft.estimateCounter || {};
  const p = (sub) => ['estimateCounter', sub];
  const pad = (n) => String(n).padStart(3, '0');   // ceros a 3 dígitos (espejo del servidor)
  const yy = ec.seedYear != null && ec.seedYear !== '' ? String(ec.seedYear).slice(-2) : '—';
  const preview = (ec.seedYear && ec.seedNumber) ? `${yy}-${pad(ec.seedNumber)}` : '—';
  const sigYear = ec.seedYear ? String(Number(ec.seedYear) + 1).slice(-2) : '—';
  const [resetting, setResetting] = useState(false);

  // Reinicia el correlativo del año sembrado: borra counters/estimates_<year> en el servidor (CF
  // resetEstimateCounter) para que la semilla vuelva a aplicar. El servidor devuelve el número real
  // que saldrá (leído de la config GUARDADA, no del draft) → por eso conviene guardar la semilla antes.
  const doReset = async () => {
    const year = Number(ec.seedYear);
    if (!year) { setSaveMsg?.({ type: 'error', text: 'Definí y guardá el año de arranque antes de reiniciar.' }); return; }
    if (!window.confirm(
      `¿Reiniciar la numeración del año ${year}? El próximo estimado de ${year} volverá a arrancar en la semilla `
      + `(${preview}). NO se borran los estimados ya emitidos, pero la numeración del año se reinicia. ¿Continuar?`)) return;
    setResetting(true); setSaveMsg?.(null);
    try {
      const res = await call('resetEstimateCounter', { year });
      setSaveMsg?.({ type: 'ok', text: `Contador de ${year} reiniciado. El próximo estimado será ${res.nextCode}`
        + `${res.lastNumberBefore != null ? ` (antes el último correlativo del año fue ${res.lastNumberBefore})` : ''}.` });
      onAfterReset?.();
    } catch (e) {
      setSaveMsg?.({ type: 'error', text: e.message || 'No se pudo reiniciar el contador (rechazado por el servidor).' });
    } finally { setResetting(false); }
  };
  return (
    <div className="space-y-4">
      <Panel title="Numeración de estimados (YY-NNN · reinicio anual)">
        <p className="text-sm text-gray-500 mb-3">
          El número de estimado se asigna en el servidor de forma atómica con formato <strong>YY-NNN</strong>
          {' '}(ej. 26-200). Siembra UNA vez el año de arranque y su correlativo inicial; el correlativo
          reinicia cada año (los años nuevos sin semilla arrancan en el valor por defecto). Los estimados
          viejos (formato E-N) no se migran.
        </p>
        <div className="flex flex-wrap gap-4">
          <Field label="Año de arranque" hint="4 dígitos"><Num value={ec.seedYear} onChange={(v) => setPath(p('seedYear'), v)} step="1" w="w-28" /></Field>
          <Field label="Correlativo inicial de ese año" hint="ej. 200"><Num value={ec.seedNumber} onChange={(v) => setPath(p('seedNumber'), v)} step="1" w="w-28" /></Field>
          <Field label="Arranque por defecto" hint="años nuevos sin semilla"><Num value={ec.defaultStart} onChange={(v) => setPath(p('defaultStart'), v)} step="1" w="w-28" /></Field>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          El primer estimado de <strong>{ec.seedYear || '—'}</strong> será <strong>{preview}</strong>;
          {' '}el siguiente {ec.seedNumber ? `${yy}-${pad(Number(ec.seedNumber) + 1)}` : '—'}. Al cambiar de año,
          reinicia solo (ej. {ec.seedYear ? `${sigYear}-${pad(ec.defaultStart || 1)}` : '—'}).
        </p>
      </Panel>
      <div className="flex justify-end">
        <button onClick={onSave} disabled={saving} className="px-5 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Guardando…' : 'Guardar contador de estimados'}
        </button>
      </div>

      {canManage && (
        <Panel title="Reiniciar numeración del año">
          <p className="text-sm text-gray-500 mb-3">
            Cambiar la semilla arriba <strong>no</strong> reinicia un año que ya tiene estimados: el servidor sigue
            sumando +1 desde el último correlativo. Si ya existe (por ejemplo) un <strong>{yy}-001</strong> y querés que
            el próximo de <strong>{ec.seedYear || '—'}</strong> sea <strong>{preview}</strong>, forzá el reinicio acá.
            Esto borra el contador del año en el servidor para que la semilla vuelva a aplicar; <strong>no</strong> borra
            los estimados ya emitidos.
          </p>
          <div className="flex items-center gap-3">
            <button onClick={doReset} disabled={resetting || !ec.seedYear}
              className="px-4 py-2 text-sm font-bold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
              {resetting ? 'Reiniciando…' : `Reiniciar numeración ${ec.seedYear || ''}`}
            </button>
            <span className="text-xs text-amber-600">⚠️ Reinicia la numeración del año. Guardá la semilla primero.</span>
          </div>
        </Panel>
      )}
    </div>
  );
}

// ════════════════════════ TAB: Bitácora (itc_events pricing.updated) ════════════
function BitacoraTab({ events, onReload }) {
  const fmt = (v) => (v && typeof v.toDate === 'function' ? v.toDate().toLocaleString() : (v ? new Date(v).toLocaleString() : ''));
  return (
    <Panel title={`Bitácora — pricing.updated (${events.length})`} right={<button type="button" onClick={onReload} className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">Recargar</button>}>
      {!events.length && <p className="text-sm text-gray-400">Aún no hay cambios de precio registrados.</p>}
      <div className="space-y-3">
        {events.map((e) => {
          const d = e.details || {};
          const changes = d.changes || [];
          const scope = d.scope || (d.sections ? d.sections.join(', ') : '') || (d.materialId ? `stock ${d.materialId}` : '');
          return (
            <div key={e.id} className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span className="font-medium text-gray-700">{scope}</span>
                <span>{e.user || 'sistema'} · {fmt(e.timestamp)}</span>
              </div>
              <ul className="mt-1 text-sm">
                {changes.slice(0, 12).map((c, i) => (
                  <li key={i} className="font-mono text-xs text-gray-600">
                    {c.path}: <span className="text-red-600">{JSON.stringify(c.oldValue)}</span> → <span className="text-green-700">{JSON.stringify(c.newValue)}</span>
                  </li>
                ))}
                {changes.length > 12 && <li className="text-xs text-gray-400">…{changes.length - 12} más</li>}
              </ul>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
